import React, { useEffect, useRef, useState } from "react";
import BrokenMedia from "components/BrokenMedia";
import { isNonImageUrl, isUnloadableImageSrc } from "utils/media";
import { reportMediaLoadFailure } from "utils/sentry/reportMediaError";

// A signed URL carries its own query string and any extra parameter invalidates
// the signature — so those cannot be cache-busted. Detected by the canonical V4
// signing parameter rather than a bare "?" check, since plenty of unsigned URLs
// carry harmless query strings that we still want to bust.
const SIGNED_URL_PARAM = /[?&]X-Goog-Signature=/i;

interface RetryImgProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  // Freshly-uploaded images land in the UNSCANNED bucket; a virus scanner then
  // moves them to the CLEAN bucket (which `file_url` points at) a few seconds
  // later — until then a GET fails (404 while the object is absent, sometimes
  // 503 mid-move). Retry the load a handful of times so the thumbnail recovers
  // once the scanner finishes, instead of staying broken.
  retries?: number;
  retryDelayMs?: number;
  // Fires once when the retry budget is spent and the image is declared dead —
  // parents that want to swap in their own UI can; without it the component
  // renders BrokenMedia itself so the failure is never invisible (REF-6063).
  onTerminalError?: () => void;
}

const RetryImg = React.forwardRef<HTMLImageElement, RetryImgProps>(({ src, retries = 10, retryDelayMs = 1200, onTerminalError, style, ...imgProps }, forwardedRef) => {
  // A src that was never a url — blank, whitespace, or a stringified JSON payload. This one is
  // decided BEFORE the first render and seeds `failed`, rather than being handled in `handleError`
  // like the non-image case: React emits a literal `src=""`, which the browser resolves against the
  // DOCUMENT url, so the moment such an <img> is committed the page's own index.html is already on
  // the wire. An effect runs too late to prevent that — the element must never mount at all.
  // (Sentry 7692940189.)
  const isUnloadable = isUnloadableImageSrc(src);

  const [attempt, setAttempt] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(isUnloadable);
  const timerRef = useRef<number>();

  // Reset retry + loaded state whenever the source changes — a CHANGE, not the first mount: a cached image is marked
  // loaded by the ref on mount, and resetting it here then hid it again (it faded in twice: a flicker on open).
  const shownSrc = useRef(src);
  useEffect(() => {
    if (shownSrc.current !== src) {
      shownSrc.current = src;
      setAttempt(0);
      setLoaded(false);
      setFailed(isUnloadable);
    }

    return () => window.clearTimeout(timerRef.current);
  }, [src, isUnloadable]);

  // No error event will ever fire for an unloadable src (no element, no request), so the terminal
  // report has to come from here instead of `handleError`. `reportMediaLoadFailure` is itself
  // once-per-session-per-key, so a StrictMode double-invoke reports once.
  // `onTerminalError` is deliberately out of the deps — callers pass an inline arrow, and a fresh
  // identity every render would re-fire the notification on every parent render. There is no
  // `exhaustive-deps` rule registered in this repo to disable, so this comment is the only guard.
  useEffect(() => {
    if (!isUnloadable) return;

    reportMediaLoadFailure({ component: "RetryImg", kind: "img", src, retries: 0, reason: "invalid_src" });
    onTerminalError?.();
  }, [src, isUnloadable]);

  // An archive or a document will be rejected by the decoder on every attempt, so the retry
  // budget can only cost 11 requests and ~12s of a blank tile before reaching the same end. Go
  // terminal on the first error instead, and report it as the distinct, actionable class: a
  // non-image url in an image element is always a wiring bug upstream of this component
  // (Sentry 7656627839 — a mannequin ZIP's `image_path` reaching the Studio background tile).
  const isNonImage = isNonImageUrl(src);

  const handleError = () => {
    if (isNonImage || attempt >= retries) {
      // Budget spent — fail VISIBLY instead of staying a transparent img forever
      // (the pre-REF-6063 behavior: every terminal failure looked like a blank tile).
      if (!failed) {
        setFailed(true);
        reportMediaLoadFailure({ component: "RetryImg", kind: "img", src, retries: isNonImage ? 0 : retries, reason: isNonImage ? "non_image" : "load" });
        onTerminalError?.();
      }

      return;
    }
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setAttempt((a) => a + 1), retryDelayMs);
  };

  // Cache-bust each retry so the browser re-requests instead of replaying the
  // cached failure. Signed URLs are exempt — appending to them breaks the
  // signature, which would turn a transient miss into a permanent one. Those
  // rely on the `key` remount below instead: a fresh element issues a fresh
  // request without touching the URL.
  const isSigned = SIGNED_URL_PARAM.test(src);
  const resolvedSrc = attempt === 0 || isSigned ? src : `${src}${src.includes("?") ? "&" : "?"}r=${attempt}`;

  if (failed) {
    return <BrokenMedia kind="img" className={imgProps.className} />;
  }

  // Stay transparent until the image actually loads — the browser's broken-image
  // glyph never flashes during retries; the parent's background shows through as a
  // plain loading placeholder, then the image fades in.
  return (
    <img
      key={`${src}#${attempt}`}
      // A cached image can finish loading before React attaches onLoad — it would then never fire and the
      // img would stay transparent forever. Catch the already-complete case on mount so it reveals.
      ref={(node) => {
        if (node?.complete && node.naturalWidth > 0) setLoaded(true);
        if (typeof forwardedRef === "function") forwardedRef(node);
        else if (forwardedRef) forwardedRef.current = node;
      }}
      src={resolvedSrc}
      onLoad={() => setLoaded(true)}
      onError={handleError}
      // The caller may provide an opacity adjustment (e.g. the canvas image controls). Keep the image fully
      // hidden until the request succeeds, then restore that caller-controlled opacity; otherwise the native
      // broken-image glyph flashes before a retry succeeds.
      style={{ ...style, opacity: loaded ? style?.opacity ?? 1 : 0, transition: "opacity 200ms ease" }}
      {...imgProps}
    />
  );
});

RetryImg.displayName = "RetryImg";

export default RetryImg;
